import { fetchFAQS } from '@api/dynamicFaq';
import { FEATURE_FLAGS, useFeatureFlags } from '@client/feature-flags';
import { setMaxResultColumns } from '@client/redux/search/actions/actionCreators';
import { FooterCategories } from '@components/footer_categories';
import { useSemanticSearch } from '@components/listings-search-bar/adapters/hooks/use-semantic-search';
import {
	fieldValueAtom,
	semanticSearchStringAtom,
} from '@components/listings-search-bar/atoms';
import Breadcrumbs from '@components/shared/Breadcrumbs';
import { useListingsFeatures } from '@feature-flags/hooks/VendorsSearch/use-listings-features';
import { useShouldShowPricingFilter } from '@feature-flags/hooks/VendorsSearch/use-should-show-pricing-filter';
import { getSortType } from '@helpers/sort';
import { useFlushImpressionsOnFilterChange } from '@hooks/useFlushImpressionsOnFilterChange';
import {
	classifierSelectedCategoryIdsAtom,
	effectiveCategoryCodeAtom,
	filterConfigsAtom,
	filterValuesAtom,
	isAllCategoriesPageAtom,
	isMultiOrNoCategoryUIAtom,
	searchQueryParamsAtom,
	shouldSkipCrossCategoryClassificationAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { useClassifierCategorySync } from '@pages/VendorsSearch/hooks/useClassifierCategorySync';
import { CATEGORY_LOADED } from '@redux/category';
import { mobileMapViewV2AssignmentSelector } from '@redux/experiments/selectors/mobile-map-view-v2';
import { clearDynamicFAQS, setDynamicFAQS } from '@redux/faq';
import { useAppDispatch } from '@redux/hooks';
import { getConversations as getConversationsThunk } from '@redux/messagedVendors/thunks';
import { setIsReferred as setIsReferredAction } from '@redux/settings';
import { requestAnonymousId } from '@redux/settings/actions';
import { PageContainer } from '@tkww/orion-web-ds-page-layout';
import { GridRow } from '@tkww/orion-web-ds-structure';
import { generateSeed } from '@utils/generateSeed';
import { Logger } from '@utils/logger/logger';
import { shouldSuppressFaqs } from '@utils/shouldSuppressFaqs';
import { SORT_DIRECTIONS } from '@utils/sortParam';
import { compose as composeCSS } from '@xo-union/react-css-modules';
import { CookieStorage } from 'cookie-storage';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import queryString from 'query-string';
import React, { type FC, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import type { SortType } from 'types/search';
import { XO_SESSION_TOKEN } from '../../../../constants/membership';
import { changeLocation } from '../actions/location';
import { fetchSearchResults, updatePage, updateSort } from '../actions/search';
import DynamicFAQ from '../components/DynamicFAQ';
import PageDescription from '../components/PageDescription';
import { SponsoredResultsDisclaimer } from '../components/SponsoredResultsDisclaimer';
import { VenueFacetLinks } from '../components/VenueFacetLinks';
import * as utils from '../utils';
import FilterPills from './FilterPills';
import GridView from './GridView';
import TopBar from './TopBar';
import Styles from './WrappedVendorsSearch.module.scss';
import { selectedOrTopVolumeCategory } from './utils';

const isMapViewSelected = (state: Redux.State) =>
	mobileMapViewV2AssignmentSelector(state) === 'map-toolbar';

const getSessionToken = () => {
	const cookieStorage = new CookieStorage();
	return cookieStorage.getItem(XO_SESSION_TOKEN);
};

export interface Params {
	categorySlug?: string;
	facet?: string;
	locationSlug: string;
	term?: string;
}

type StateProps = ReturnType<typeof mapStateToProps>;

interface DispatchProps {
	changeLocation: (location: { city: string; stateCode: string }) => void;
	fetchSearchResults: (opts: unknown) => Promise<unknown>;
	getConversations: (memberId: string, sessionToken: string) => void;
	isReferred: (value: boolean) => void;
	requestAnonymousId: () => void;
	updatePage: (page: number) => void;
	updateSort: (sort: SortType | string) => void;
}

type WrappedVendorsSearchProps = StateProps & DispatchProps;

// Module-level flag: only skip fetch on the very first mount (SSR hydration).
// Survives component remounts from route changes so subsequent mounts still fetch.
let hasHydrated = false;
let hasHydratedFaqs = false;

// A later return to the hydrated location is a real navigation once any single-category page was visited.
let hasVisitedSingleCategoryPage = false;

// Lets re-runs on the SSR-hydrated all-categories page preserve its FAQ instead of clearing it.
let hydratedAllCategoriesLocationSlug: string | null = null;

// Single classifier category the SSR FAQ was built for; a different selection means that FAQ no longer applies.
let hydratedClassifierFaqGuid: string | null = null;

// Category whose hydrated FAQ a suppressed sort withheld or cleared, so an eligible sort can restore it.
let hydratedSuppressedFaqGuid: string | null = null;

/** @knipignore test-only — reset hydration flag between tests */
export const _resetHasHydrated = () => {
	hasHydrated = false;
	hasHydratedFaqs = false;
	hasVisitedSingleCategoryPage = false;
	hydratedAllCategoriesLocationSlug = null;
	hydratedSuppressedFaqGuid = null;
	hydratedClassifierFaqGuid = null;
};

/** @knipignore test-only — reset search cache between tests */
export const _resetSearchCache = () => {
	lastSearchCacheKey = null;
};

// Helper to create a deterministic cache key from all tracked search parameters.
// Using an array + JSON.stringify keeps the ordering explicit and stable, preventing
// false cache misses from accidental reordering of key parts.

const createSearchCacheKey = (
	searchQueryParams: string,
	categoryCode: string,
	pageNumber: number,
	normalizedSortParam: string | undefined,
	locationSlug: string,
	searchString: string,
	shouldSkipCrossCategoryClassification: boolean,
): string => {
	return JSON.stringify([
		searchQueryParams,
		categoryCode,
		pageNumber,
		normalizedSortParam,
		locationSlug,
		searchString,
		shouldSkipCrossCategoryClassification,
	]);
};

// Module-level cache: track the last search executed to skip duplicates during remounts.
// Includes all search input dimensions (location, filters, pagination, sort, semantic search)
// to ensure the duplicate guard doesn't false-positive when user navigates location or
// changes semantic search while filters/page/sort remain the same.
//
// Does NOT track: anonymousId, member?.id, shouldShowPricingFilter, shouldFetchPriceRangeIntervals,
// or the derived isAllCategoriesPage/categoryGuid combination. These are intentionally excluded because:
// - Member context (anonymousId, member?.id) only changes on login/logout (full page reload)
// - Feature flags only change on page reload, not during active search sessions
// - isAllCategoriesPage/categoryGuid are derived from categoryCode/searchQueryParams, so any change
//   to them would cause a cache miss anyway through those tracked fields
// The cache is designed to prevent duplicate fetches during component remounts (common), not to track
// all session state changes (rare and mostly handled by other fields).
let lastSearchCacheKey: string | null = null;

const WrappedVendorsSearch: FC<WrappedVendorsSearchProps> = (props) => {
	const {
		categoryCode,
		categoryGuid,
		changeLocation,
		city,
		fetchSearchResults,
		getConversations,
		isReferred,
		member,
		page,
		sort,
		showMapView,
		stateCode,
		updatePage,
		updateSort,
		anonymousId,
	} = props;

	const location = useLocation();
	const params: Params = useParams();
	const [fieldValue, setFieldValue] = useAtom(fieldValueAtom);
	const searchQueryParams = useAtomValue(searchQueryParamsAtom);
	const shouldSkipCrossCategoryClassification = useAtomValue(
		shouldSkipCrossCategoryClassificationAtom,
	);
	const filterConfigs = useAtomValue(filterConfigsAtom);
	const searchString = useAtomValue(semanticSearchStringAtom);
	const reduxDispatch = useAppDispatch();
	const { handleSemanticSearch } = useSemanticSearch();
	const shouldShowPricingFilter = useShouldShowPricingFilter();
	/** Counts how many fetch-effect invocations to skip (classifier pre-selection). */
	const skipNextFetchRef = useRef(0);
	const latestFaqRequestRef = useRef(0);
	const classifierCategoryIds = useAtomValue(classifierSelectedCategoryIdsAtom);
	const classifierFaqCategoryGuid =
		classifierCategoryIds?.length === 1 ? classifierCategoryIds[0] : null;
	const isAllCategoriesPage = !params.categorySlug;
	const setIsAllCategoriesPage = useSetAtom(isAllCategoriesPageAtom);
	const isMultiOrNoCategory = useAtomValue(isMultiOrNoCategoryUIAtom);
	const effectiveCategoryCode = useAtomValue(effectiveCategoryCodeAtom);
	const shouldHideCategoryFooter = isMultiOrNoCategory;
	const filterValues = useAtomValue(filterValuesAtom);
	const topCategory = React.useMemo(() => {
		// Single-category URL: category comes from the route slug (no ?category= param).
		if (params.categorySlug && filterConfigs?.category?.filterOptions) {
			return filterConfigs.category.filterOptions.find(
				(o) => o.plural.slug === params.categorySlug,
			);
		}
		return selectedOrTopVolumeCategory(filterValues, filterConfigs);
	}, [params.categorySlug, filterConfigs, filterValues]);

	// Keep isAllCategoriesPageAtom in sync with the URL on client-side navigation.
	// categoryFilters.ts only runs server-side, so the atom is stale after
	// history.push between /marketplace/wichita-ks and /marketplace/djs-wichita-ks.
	useEffect(() => {
		setIsAllCategoriesPage(isAllCategoriesPage);
	}, [isAllCategoriesPage, setIsAllCategoriesPage]);

	useFlushImpressionsOnFilterChange();

	// Sync Redux category state on client-side navigation to a single-category
	// URL while cross-category is active. citySearchPageFetch only runs server-
	// side, so after history.push('/marketplace/:cat-:loc') the Redux category
	// remains the SSR value. fetchBase reads getState().category.code directly,
	// so it would search the wrong category without this update.
	// NOTE: Effect 2 is guarded by the effectiveCategoryCode mismatch check, so
	// it skips the transitional render (categoryCode still stale). The search
	// fires exactly once — after this dispatch resolves categoryCode.
	useEffect(() => {
		if (!topCategory) return;
		if (topCategory.code === categoryCode) return;
		// Mirror what setCategory(slugToInfo, slug) dispatches: for single
		// category searches set to that category, for multi-category searches
		// set to category by highest vendor volume.
		reduxDispatch({
			type: CATEGORY_LOADED,
			payload: {
				id: topCategory.id,
				code: topCategory.code,
				name: topCategory.name,
				seo: {
					singular: {
						slug: topCategory.singular.slug,
						term: topCategory.singular.term,
					},
					plural: {
						slug: topCategory.plural.slug,
						term: topCategory.plural.term,
					},
				},
			},
		});
		reduxDispatch(setMaxResultColumns(3));
	}, [reduxDispatch, topCategory, categoryCode]);

	useEffect(() => {
		requestAnonymousId();
		isReferred(true);
	}, [isReferred]);

	useEffect(() => {
		if (member?.id) {
			const sessionToken = getSessionToken();
			if (sessionToken) {
				getConversations(member.id, sessionToken);
			}
		}
	}, [getConversations, member?.id]);

	useEffect(() => {
		if (params.term) {
			const searchTerm = params.term.split('-').join(' ');
			setFieldValue(searchTerm);
			handleSemanticSearch({ searchTerm });
		}
	}, [handleSemanticSearch, params.term, setFieldValue]);

	useListingsFeatures();

	useClassifierCategorySync({ skipNextFetchRef });

	const featureFlags = useFeatureFlags();
	const shouldFetchPriceRangeIntervals =
		featureFlags[FEATURE_FLAGS.LISTINGS_PRICING];

	// Memoize parsed query params to prevent unnecessary re-parsing
	const queryParams = React.useMemo(() => {
		return queryString.parse(location.search);
	}, [location.search]);

	const pageNumber = Number(queryParams.page || 1);
	const sortParam = queryParams.sort;

	// Normalize sort parameter: treat undefined/null and 'recommended' as the same
	// This prevents double fetches when sort=recommended is added to URL (it's the default)
	const normalizedSortParam = React.useMemo(() => {
		if (!sortParam || sortParam === 'recommended') {
			return 'recommended';
		}
		return typeof sortParam === 'string' ? sortParam : String(sortParam);
	}, [sortParam]);

	// Mirrors the server's getSortType so a blocklisted market with no ?sort resolves to 'featured' on both sides.
	const faqSort =
		getSortType(
			typeof sortParam === 'string' ? sortParam : '',
			params.locationSlug,
		) ?? normalizedSortParam;

	useEffect(() => {
		if (!hasHydratedFaqs) {
			hasHydratedFaqs = true;
			if (isAllCategoriesPage) {
				// Don't clear here — may be SSR-rendered classifier FAQ content; the next pass decides.
				hydratedAllCategoriesLocationSlug = params.locationSlug;
				hydratedClassifierFaqGuid = classifierFaqCategoryGuid;
				hydratedSuppressedFaqGuid = shouldSuppressFaqs(
					classifierFaqCategoryGuid,
					faqSort,
				)
					? classifierFaqCategoryGuid
					: null;
				return;
			}
			hasVisitedSingleCategoryPage = true;
			return () => {
				reduxDispatch(clearDynamicFAQS());
			};
		}

		latestFaqRequestRef.current += 1;
		const requestId = latestFaqRequestRef.current;
		let didCancel = false;

		if (isAllCategoriesPage) {
			// Classifier arrivals here don't describe the prior category, so clear rather than restore stale FAQ/JSON-LD.
			if (
				!hasVisitedSingleCategoryPage &&
				hydratedAllCategoriesLocationSlug === params.locationSlug &&
				classifierFaqCategoryGuid === hydratedClassifierFaqGuid
			) {
				if (shouldSuppressFaqs(classifierFaqCategoryGuid, faqSort)) {
					hydratedSuppressedFaqGuid = classifierFaqCategoryGuid;
					reduxDispatch(clearDynamicFAQS());
					return;
				}
				// Still the page SSR hydrated as all-categories — preserve any classifier FAQ content.
				const restoreGuid = hydratedSuppressedFaqGuid;
				if (!restoreGuid) {
					return;
				}
				const hydratedLocation = utils.getLocationFromSlug(
					params.locationSlug || 'new-york-ny',
				);
				fetchFAQS(hydratedLocation, restoreGuid)
					.then((result) => {
						if (!didCancel && latestFaqRequestRef.current === requestId) {
							hydratedSuppressedFaqGuid = null;
							reduxDispatch(setDynamicFAQS(result));
						}
					})
					.catch((err: unknown) => {
						if (!didCancel) {
							hydratedSuppressedFaqGuid = null;
						}
						Logger.error({
							log_message: `Failed to restore directory FAQs for categoryId="${restoreGuid}"`,
							errorToLog: err instanceof Error ? err : new Error(String(err)),
							newRelic: true,
						});
					});
				return () => {
					didCancel = true;
				};
			}
			hydratedAllCategoriesLocationSlug = null;
			hydratedSuppressedFaqGuid = null;
			hydratedClassifierFaqGuid = null;
			reduxDispatch(clearDynamicFAQS());
			return;
		}

		// topCategory can be unavailable until filter config hydrates; Redux category is already the route's then.
		if (
			topCategory &&
			(topCategory.id !== categoryGuid || topCategory.code !== categoryCode)
		) {
			return;
		}

		const faqLocation = utils.getLocationFromSlug(
			params.locationSlug || 'new-york-ny',
		);

		if (!faqLocation.city || !faqLocation.stateCode) {
			return;
		}

		hasVisitedSingleCategoryPage = true;

		// The preserve branch above registers no cleanup, so a rejected fetch below would leave the old FAQ/JSON-LD attached.
		reduxDispatch(clearDynamicFAQS());

		if (shouldSuppressFaqs(categoryGuid, faqSort)) {
			return;
		}

		fetchFAQS(faqLocation, categoryGuid)
			.then((result) => {
				if (!didCancel && latestFaqRequestRef.current === requestId) {
					reduxDispatch(setDynamicFAQS(result));
				}
			})
			.catch((err: unknown) => {
				Logger.error({
					log_message: `Failed to fetch directory FAQs for categoryId="${categoryGuid}"`,
					errorToLog: err instanceof Error ? err : new Error(String(err)),
					newRelic: true,
				});
			});

		return () => {
			didCancel = true;
			reduxDispatch(clearDynamicFAQS());
		};
	}, [
		categoryCode,
		categoryGuid,
		classifierFaqCategoryGuid,
		isAllCategoriesPage,
		faqSort,
		params.locationSlug,
		reduxDispatch,
		topCategory,
	]);

	// Effect 1: Sync Redux state when URL params change
	// This keeps Redux in sync with URL without triggering searches
	useEffect(() => {
		const userLocation = utils.getLocationFromSlug(
			params.locationSlug || 'new-york-ny',
		);

		if (userLocation.city !== city || userLocation.stateCode !== stateCode) {
			changeLocation(userLocation);
		}

		if (page !== pageNumber) {
			updatePage(pageNumber);
		}

		const normalizedSort =
			typeof sortParam === 'string'
				? sortParam || SORT_DIRECTIONS.recommended.type
				: SORT_DIRECTIONS.recommended.type;

		if (sort !== normalizedSort) {
			updateSort(normalizedSort);
		}
	}, [
		params.locationSlug,
		city,
		stateCode,
		page,
		pageNumber,
		sortParam,
		sort,
		changeLocation,
		updatePage,
		updateSort,
	]);

	// Effect 2: Fetch search results when search parameters change
	// Only triggered by actual search parameter changes, not URL navigation
	useEffect(() => {
		// Skip only the very first mount (SSR hydration) to prevent double fetch.
		// Subsequent remounts from route changes should still fetch.
		if (!hasHydrated) {
			hasHydrated = true;
			return;
		}

		// Skip when the classifier has pre-selected categories to avoid re-fetching
		// results that the backend already returned with classifier-selected categories.
		// Single-category sync from a category page: skips 2 runs (URL path change +
		// CATEGORY_LOADED’s categoryCode change). Single-category sync from the
		// all-categories page: skips 3 runs. Multi-category sync: skips 1 run.
		if (skipNextFetchRef.current > 0) {
			skipNextFetchRef.current -= 1;
			return;
		}

		// Guard: Skip fetch if this is a remount with identical search params (e.g., duplicate from component remount).
		// Check all dimensions: filters, category, pagination, sort, location, and semantic search.
		const currentSearchKey = JSON.stringify(searchQueryParams);
		const locationSlugKey = params.locationSlug || 'new-york-ny';
		const currentCacheKey = createSearchCacheKey(
			currentSearchKey,
			categoryCode,
			pageNumber,
			normalizedSortParam,
			locationSlugKey,
			searchString,
			shouldSkipCrossCategoryClassification,
		);
		if (lastSearchCacheKey === currentCacheKey) {
			return;
		}

		// Guard: Skip if Jotai's effective category (from filter selection) and
		// Redux's categoryCode disagree — this is the transitional window where
		// the user selected a single category but setCategoryFilters hasn't
		// resolved yet. The correct search will fire once Redux catches up.
		if (effectiveCategoryCode !== categoryCode) {
			return;
		}

		if (
			!isAllCategoriesPage &&
			!searchQueryParams.filters.includes(categoryGuid)
		) {
			return;
		}

		// Update cache with this search (normalize location slug to match the default used in fetchSearchResults)
		lastSearchCacheKey = currentCacheKey;
		const userLocation = utils.getLocationFromSlug(
			params.locationSlug || 'new-york-ny',
		);

		fetchSearchResults({
			...searchQueryParams,
			categoryCode,
			categoryGuid: isAllCategoriesPage ? undefined : categoryGuid,
			fixedPageSize: isMultiOrNoCategory,
			location: userLocation,
			page: pageNumber,
			sort: normalizedSortParam,
			seed: generateSeed(),
			member: { anonymousId, userId: member?.id },
			shouldShowPricingFilter,
			...(searchString ? { searchString } : {}),
			...(shouldSkipCrossCategoryClassification
				? { skipCrossCategoryClassification: true }
				: {}),
			shouldFetchPriceRangeIntervals,
		});
	}, [
		// Core search parameters - these are the ONLY things that should trigger a new search
		searchQueryParams, // All filter changes (includes /affordable removal)
		categoryCode, // Category changes (Redux)
		effectiveCategoryCode, // Category code derived from Jotai filter state — triggers re-eval of transitional guard
		pageNumber, // Pagination changes
		normalizedSortParam, // Sort changes (normalized to prevent false positives)
		shouldSkipCrossCategoryClassification,
		// Data dependencies needed for the fetch
		isAllCategoriesPage,
		isMultiOrNoCategory,
		categoryGuid,
		params.locationSlug,
		anonymousId,
		member?.id,
		shouldShowPricingFilter,
		searchString,
		fetchSearchResults,
		shouldFetchPriceRangeIntervals,
	]);

	return (
		<PageContainer
			classes={composeCSS({
				'top-level-container': Styles.vendorSearchContainer, // Look to deprecate this, as it is not suggested to use it
			})}
		>
			<TopBar />
			<FilterPills
				match={{
					params: {
						facet: params.facet || '',
						locationSlug: params.locationSlug || 'new-york-ny',
					},
				}}
				searchLocation={location.search}
				showMapView={showMapView}
			/>
			<SponsoredResultsDisclaimer />
			<GridView />
			{!shouldHideCategoryFooter && <VenueFacetLinks />}
			<FooterCategories city={city || ''} stateCode={stateCode || ''} />
			<GridRow>
				<div className={Styles.faqAndPageWrapper} />
			</GridRow>
			<DynamicFAQ />
			<PageDescription />
			{!shouldHideCategoryFooter && (
				<Breadcrumbs
					className={Styles.footerBreadcrumb}
					categoryCode={categoryCode}
					city={city}
					stateCode={stateCode}
				/>
			)}
		</PageContainer>
	);
};

function mapStateToProps(state: Redux.State) {
	const {
		category: { id, code },
		location: { city, stateCode },
		search: {
			filterPills: { applied },
			pagination: { page },
			marketCode,
		},
	} = state;
	const sort = state.search.sort || SORT_DIRECTIONS.recommended.type;

	return {
		applied,
		categoryGuid: id,
		categoryCode: code,
		city,
		marketCode,
		member: state.membership.member,
		page,
		sort,
		stateCode,
		showMapView: isMapViewSelected(state),
		anonymousId: state.settings.anonymousId,
	};
}

const mapDispatchToProps = {
	changeLocation,
	fetchSearchResults,
	getConversations: getConversationsThunk,
	isReferred: setIsReferredAction,
	requestAnonymousId,
	updatePage,
	updateSort,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(WrappedVendorsSearch);
